/**
 * Maps enriched GlobalSearch / GlobalSearchWithFilter items onto flat
 * display fields the search UI already consumes (plus a few extras).
 */

const parseTypeAndFollowers = (typeValue) => {
  const [type, followersRaw] = String(typeValue || "").split(",");
  const followers = parseInt(followersRaw, 10);
  return {
    type: type?.trim() || "",
    followersFromType: Number.isFinite(followers) ? followers : undefined,
  };
};

const getAuthor = (item) => {
  if (item?.author && typeof item.author === "object") return item.author;
  return null;
};

/**
 * @param {object} item - raw searchdata entry
 * @returns {object} item with type normalized and nested info flattened
 */
export function mapGlobalSearchItem(item = {}) {
  const { type, followersFromType } = parseTypeAndFollowers(item.type);
  const people = item.peopleInfo || {};
  const job = item.jobInfo || {};
  const company = item.companyInfo || {};
  const hashtag = item.hashtagInfo || {};
  const author = getAuthor(item);
  const stats = item.contentStats || {};
  const article = item.articleInfo || {};

  const followers =
    people.followersCount ??
    item.followersCount ??
    followersFromType;

  const mapped = {
    ...item,
    type,
    followers: Number.isFinite(followers) ? followers : undefined,
    createdOn: item.createdOn || item.creationTime || item.date,
    creationTime: item.creationTime || item.createdOn || item.date,
  };

  if (type === "User") {
    const headline = people.headline || item.headline || "";
    const summary = people.summary || item.summary || "";
    return {
      ...mapped,
      title: item.title || people.fullName || people.name || "",
      image: people.profilePic || item.image,
      displayName: people.fullName || undefined,
      userName: people.userName || undefined,
      headline,
      summary,
      // Prefer headline, then summary, for the compact subtitle line
      additionalInfo:
        headline || summary || item.additionalInfo || null,
      description: people.location || item.description || "",
      location: people.location || item.location || "",
      isFollowedByYou:
        people.isFollowedByYou ?? item.isFollowedByYou ?? false,
      isConnectedByYou:
        people.isConnectedByYou ?? item.isConnectedByYou ?? null,
      mutualConnectionsCount: people.mutualConnectionsCount ?? 0,
    };
  }

  if (type === "Job") {
    const companyName = job.companyName || item.companyName || item.compName || "";
    const location = job.location || item.description || "";
    const salary = job.salaryDisplay || item.salaryDisplay || "";
    return {
      ...mapped,
      id: item.id || job.jobId,
      jobId: job.jobId || item.jobId || item.id,
      title: item.title || job.title || job.jobTitle || "",
      image: job.companyLogo || item.image,
      additionalInfo: job.designation || item.additionalInfo || null,
      description: location,
      location,
      companyName,
      compName: companyName,
      jobType: job.jobType || item.jobType || item.jbTypName || "",
      jbTypName: job.jobType || item.jbTypName || item.jobType || "",
      workType: job.workLocation || item.workType || item.workLocationTypeName || "",
      workLocationTypeName:
        job.workLocation || item.workLocationTypeName || item.workType || "",
      salaryDisplay: salary,
      shortDescription: item.shortDescription || "",
      isExpired: job.isExpired ?? item.isExpired,
      isApplied:
        job.isApplied ??
        job.isAppliedByYou ??
        item.isApplied ??
        item.isAppliedByYou ??
        false,
      isSaved:
        job.isSaved ??
        job.isSavedByYou ??
        item.isSaved ??
        item.isSavedByYou ??
        false,
      expiringInDays: job.expiringInDays ?? item.expiringInDays,
    };
  }

  if (type === "Post" || type === "Article") {
    const authorName =
      author?.name ||
      (typeof item.author === "string" ? item.author : "") ||
      "";
    const mediaThumb =
      item.media?.[0]?.thumbnail || item.media?.[0]?.url || null;
    const banner = article.banner || item.image || mediaThumb;

    return {
      ...mapped,
      image:
        type === "Article"
          ? banner || author?.profilePic || item.image
          : author?.profilePic || mediaThumb || item.image,
      media: Array.isArray(item.media) ? item.media : [],
      contentImage: mediaThumb,
      articleBanner: type === "Article" ? banner : null,
      author: authorName || item.additionalInfo || "",
      authorProfilePic: author?.profilePic || null,
      additionalInfo: authorName || item.additionalInfo || null,
      userUid: author?.id || item.userUid || null,
      nameTag: author?.userName || item.nameTag || null,
      content: item.content || item.shortDescription || "",
      shortDescription: item.shortDescription || item.content || "",
      likeCount: stats.likeCount ?? item.likeCount,
      commentCount: stats.commentCount ?? item.commentCount,
      shareCount: stats.shareCount ?? item.shareCount,
      isLikedByYou: stats.isLikedByYou ?? item.isLikedByYou,
      isSavedByYou: stats.isSavedByYou ?? item.isSavedByYou,
      isFollowedByYou: author?.isFollowedByYou ?? item.isFollowedByYou,
      // Keep description as a date string when API still sends one
      description: item.description || "",
    };
  }

  if (type === "Blog") {
    return {
      ...mapped,
      category: item.category || "",
      shortDescription: item.shortDescription || "",
      // Keep additionalInfo as slugURLvalue for blog detail route
      additionalInfo: item.additionalInfo || item.slug || null,
    };
  }

  if (type === "Company") {
    return {
      ...mapped,
      title: item.title || company.name || company.companyName || "",
      image: company.logo || company.companyLogo || item.image,
      headline:
        company.industry ||
        company.headline ||
        item.headline ||
        item.additionalInfo ||
        "",
      summary:
        company.about ||
        company.summary ||
        item.summary ||
        item.shortDescription ||
        "",
      location: company.location || item.location || item.description || "",
      description: company.location || item.description || "",
      followers:
        company.followersCount ??
        item.followersCount ??
        followersFromType ??
        mapped.followers,
      isFollowedByYou:
        company.isFollowedByYou ?? item.isFollowedByYou ?? false,
    };
  }

  if (type === "Hashtag") {
    return {
      ...mapped,
      title:
        item.title ||
        hashtag.displayName ||
        hashtag.name ||
        (item.slug ? `#${item.slug}` : ""),
      description:
        hashtag.description ||
        item.description ||
        item.additionalInfo ||
        "",
      postCount:
        hashtag.postCount ?? item.postCount ?? item.contentCount,
    };
  }

  return mapped;
}

export function mapGlobalSearchList(list = []) {
  if (!Array.isArray(list)) return [];
  return list.map(mapGlobalSearchItem);
}
